import { ReactNode, useEffect, useRef } from 'react';
import Ably from 'ably';
import { AblyProvider } from 'ably/react';
import { ABLY_AUTH_URL } from 'common/config/app';
import { getStoredAccessToken } from 'pages/Auth/utils';

interface AblyContextProviderProps {
  children: ReactNode;
}

const AblyContextProvider = ({ children }: AblyContextProviderProps) => {
  const clientRef = useRef<Ably.Realtime | null>(null);

  // Only assign when clientRef.current is null or undefined
  clientRef.current ??= new Ably.Realtime({
    // Cloned E2E users share external user:* channels. Keep the real Notification
    // UI mounted for injection tests without receiving other runs' live events.
    // Ordinary app builds retain Ably's default connection behavior.
    autoConnect: process.env.REACT_APP_ENABLE_E2E_HOOKS !== 'true',
    authCallback: async (_data, callback) => {
      try {
        const accessToken = getStoredAccessToken();
        if (!accessToken) {
          callback('Auth failed: no access token available', null);
          return;
        }
        const response = await fetch(ABLY_AUTH_URL, {
          method: 'GET',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) {
          callback(`Auth failed: ${response.status}`, null);
          return;
        }
        const { token } = await response.json();
        callback(null, token);
      } catch (err) {
        callback(String(err), null);
      }
    },
  });

  useEffect(() => () => {
    clientRef.current?.close();
    clientRef.current = null;
  }, []);

  return <AblyProvider client={clientRef.current}>{children}</AblyProvider>;
};

export default AblyContextProvider;
